import type { BrandTokens } from "../brand/tokens";
import {
  CHART_LABEL_MAX_LENGTH,
  CHART_MAX_LABELS,
  CHART_MAX_SERIES,
  type ChartKind,
} from "./chart-schema";
import { opaqueHex, parseHex, relativeLuminance, toHex } from "./color";
import type { ChartElement, ChartSeries } from "./schema";

/**
 * Charts: colours, German number formatting, pasting tables from Excel and
 * the geometry. `chartLayout` turns a chart element into a flat list of
 * vector primitives (paths, circles, lines, texts) in element pixels; the
 * renderer only maps them to SVG. Pure and deterministic, so editor,
 * thumbnails, PNG/PDF export and the print PDF draw exactly the same chart.
 */

export * from "./chart-schema";

// ---------------------------------------------------------------------------
// Colours
// ---------------------------------------------------------------------------

/** Default palette: clear, well distinguishable hues that also print well. */
export const DEFAULT_CHART_PALETTE = [
  "#4263eb",
  "#f59f00",
  "#12b886",
  "#f03e3e",
  "#7048e8",
  "#1c7ed6",
  "#e64980",
  "#74b816",
] as const;

/** Mixes two opaque colours (`amount` 0 = a, 1 = b). */
export function mixColors(a: string, b: string, amount: number): string {
  const x = parseHex(a);
  const y = parseHex(b);
  const t = Math.max(0, Math.min(1, amount));
  return toHex({
    r: x.r + (y.r - x.r) * t,
    g: x.g + (y.g - x.g) * t,
    b: x.b + (y.b - x.b) * t,
    a: 1,
  });
}

const NON_SERIES_ROLES = new Set(["background", "surface", "text", "text-muted"]);

/**
 * Chart palette from a brand kit: primary, secondary, accent and custom
 * colours (not background or text colours), topped up with lighter tints so
 * there are at least four colours.
 */
export function brandChartPalette(tokens: BrandTokens): string[] | null {
  const order = ["primary", "secondary", "accent"];
  const colors = [...tokens.colors]
    .filter((color) => !NON_SERIES_ROLES.has(color.key))
    .sort((a, b) => {
      const ia = order.indexOf(a.key);
      const ib = order.indexOf(b.key);
      return (ia < 0 ? 99 : ia) - (ib < 0 ? 99 : ib);
    })
    .map((color) => opaqueHex(color.color));
  const unique = [...new Set(colors)].slice(0, 8);
  if (unique.length === 0) return null;
  const base = [...unique];
  for (let i = 0; unique.length < 4; i++) {
    const source = base[i % base.length] as string;
    unique.push(mixColors(source, "#ffffff", i < base.length ? 0.45 : 0.7));
  }
  return unique;
}

/** Colour of the series (or pie slice) at `index`. */
export function chartColor(element: Pick<ChartElement, "palette">, index: number): string {
  const palette =
    element.palette && element.palette.length > 0 ? element.palette : DEFAULT_CHART_PALETTE;
  return palette[index % palette.length] as string;
}

export function seriesColor(
  element: Pick<ChartElement, "palette">,
  series: ChartSeries,
  index: number,
): string {
  return series.color ?? chartColor(element, index);
}

/** Pie, donut and progress charts colour their slices, the others their series. */
export function isRadialChart(kind: ChartKind): boolean {
  return kind === "pie" || kind === "donut" || kind === "progress";
}

// ---------------------------------------------------------------------------
// Numbers
// ---------------------------------------------------------------------------

/**
 * German number formatting without Intl (identical everywhere):
 * `1234.5` → „1.234,5“, with a unit „12 %“ (no-break space before the unit).
 */
export function formatChartNumber(value: number, decimals = 0, unit = ""): string {
  const digits = Math.max(0, Math.min(4, Math.round(decimals)));
  const fixed = Math.abs(value).toFixed(digits);
  const [whole = "0", fraction] = fixed.split(".");
  const grouped = whole.replace(/\B(?=(\d{3})+(?!\d))/g, ".");
  const negative = value < 0 && Number(fixed) !== 0;
  const text = `${negative ? "−" : ""}${grouped}${fraction ? `,${fraction}` : ""}`;
  const suffix = unit.trim();
  return suffix ? `${text} ${suffix}` : text;
}

/**
 * Reads a number the way people type it in Germany (and elsewhere):
 * „1.234,5“, „1,234.5“, „12,5 %“, „−3“, „€ 1.200“. Returns null for text.
 */
export function parseChartNumber(input: string): number | null {
  let text = input
    .trim()
    .replace(/[−–]/g, "-")
    .replace(/[\s  '’]/g, "")
    .replace(/[%€$£¥]|EUR|USD|CHF/gi, "");
  if (/^\(.*\)$/.test(text)) text = `-${text.slice(1, -1)}`;
  if (!/^[+-]?[\d.,]*\d[\d.,]*$/.test(text)) return null;
  const comma = text.lastIndexOf(",");
  const dot = text.lastIndexOf(".");
  if (comma >= 0 && dot >= 0) {
    // The later one is the decimal separator.
    text = comma > dot ? text.replace(/\./g, "").replace(",", ".") : text.replace(/,/g, "");
  } else if (comma >= 0) {
    // Several commas: thousands (1,234,567); one comma: German decimal comma.
    text = (text.match(/,/g)?.length ?? 0) > 1 ? text.replace(/,/g, "") : text.replace(",", ".");
  } else if (dot >= 0 && /^[+-]?\d{1,3}(\.\d{3})+$/.test(text)) {
    // „1.234“ / „12.500.000“: German thousands separators.
    text = text.replace(/\./g, "");
  }
  const value = Number(text);
  return Number.isFinite(value) ? value : null;
}

// ---------------------------------------------------------------------------
// Tables (paste from Excel, Numbers, Google Sheets, CSV)
// ---------------------------------------------------------------------------

export interface ChartData {
  labels: string[];
  series: { name: string; values: (number | null)[] }[];
}

function splitRow(line: string, delimiter: string): string[] {
  const cells: string[] = [];
  let cell = "";
  let quoted = false;
  for (let i = 0; i < line.length; i++) {
    const char = line[i];
    if (quoted) {
      if (char === '"' && line[i + 1] === '"') {
        cell += '"';
        i++;
      } else if (char === '"') quoted = false;
      else cell += char;
    } else if (char === '"' && cell.trim() === "") {
      quoted = true;
      cell = "";
    } else if (char === delimiter) {
      cells.push(cell.trim());
      cell = "";
    } else cell += char;
  }
  cells.push(cell.trim());
  return cells;
}

/**
 * Parses a pasted table: tab-separated (Excel, Numbers, Sheets), semicolon
 * (German CSV) or comma separated. Rows are labels, columns are series; a
 * header row gives the series names, a text column the labels. Values may use
 * a decimal comma. Returns null when there is no number at all.
 */
export function parseChartTable(text: string): ChartData | null {
  const lines = text
    .replace(/\r\n?/g, "\n")
    .split("\n")
    .filter((line) => line.trim() !== "");
  if (lines.length === 0) return null;
  const head = lines[0] as string;
  const delimiter = lines.some((line) => line.includes("\t"))
    ? "\t"
    : lines.some((line) => line.includes(";"))
      ? ";"
      : head.includes(",") && parseChartNumber(head) === null
        ? ","
        : "\t";
  let rows = lines.map((line) => splitRow(line, delimiter));
  const width = Math.max(...rows.map((row) => row.length));
  rows = rows.map((row) => [...row, ...Array<string>(width - row.length).fill("")]);

  const isNumber = (cell: string) => cell !== "" && parseChartNumber(cell) !== null;
  const first = rows[0] ?? [];
  // Header row: no numbers after the first cell, an empty corner cell (as
  // copied from Excel) or years as series names („Monat;2025;2026“).
  const isYear = (cell: string) => /^(19|20)\d\d$/.test(cell);
  const rest = first.slice(1).filter((cell) => cell !== "");
  const hasHeader =
    rows.length > 1 &&
    (rest.length === 0
      ? !isNumber(first[0] as string)
      : rest.every((cell) => !isNumber(cell)) ||
        first[0] === "" ||
        (!isNumber(first[0] as string) && rest.every(isYear)));
  const body = hasHeader ? rows.slice(1) : rows;
  // Label column: the first column holds text (or there is only one column of text).
  const hasLabels = width > 1 && body.some((row) => row[0] !== "" && !isNumber(row[0] as string));
  const startColumn = hasLabels ? 1 : 0;
  const columns: number[] = [];
  for (let column = startColumn; column < width; column++) {
    if (body.some((row) => isNumber(row[column] as string))) columns.push(column);
  }
  if (columns.length === 0) return null;
  const limited = body.slice(0, CHART_MAX_LABELS);
  const clip = (value: string) => value.slice(0, CHART_LABEL_MAX_LENGTH);
  return {
    labels: limited.map((row, index) => clip(hasLabels ? (row[0] as string) : String(index + 1))),
    series: columns.slice(0, CHART_MAX_SERIES).map((column, index) => ({
      name: clip(hasHeader && first[column] ? (first[column] as string) : `Reihe ${index + 1}`),
      values: limited.map((row) => parseChartNumber(row[column] as string)),
    })),
  };
}

/** The chart's data as a tab-separated table (for copying back into Excel). */
export function chartToTable(element: Pick<ChartElement, "labels" | "series">): string {
  const format = (value: number | null | undefined) =>
    value === null || value === undefined ? "" : String(value).replace(".", ",");
  const header = ["", ...element.series.map((series) => series.name)].join("\t");
  const rows = element.labels.map((label, index) =>
    [label, ...element.series.map((series) => format(series.values[index]))].join("\t"),
  );
  return [header, ...rows].join("\n");
}

// ---------------------------------------------------------------------------
// Sample data for new charts
// ---------------------------------------------------------------------------

export function sampleChartData(kind: ChartKind): Pick<ChartElement, "labels" | "series"> & {
  title: string;
  unit: string;
  stacked?: boolean;
} {
  switch (kind) {
    case "pie":
    case "donut":
      return {
        title: "Marktanteile",
        unit: "%",
        labels: ["Online", "Filiale", "Partner", "Sonstige"],
        series: [{ name: "Anteil", values: [42, 28, 18, 12] }],
      };
    case "progress":
      return {
        title: "",
        unit: "%",
        labels: ["Ziel erreicht"],
        series: [{ name: "Fortschritt", values: [72] }],
      };
    case "line":
    case "area":
      return {
        title: "Besucher pro Monat",
        unit: "",
        labels: ["Jan", "Feb", "Mär", "Apr", "Mai", "Jun"],
        series: [
          { name: "2025", values: [1200, 1350, 1280, 1620, 1810, 2050] },
          { name: "2026", values: [1450, 1600, 1720, 1900, 2240, 2480] },
        ],
      };
    case "bar":
      return {
        title: "Beliebteste Produkte",
        unit: "",
        labels: ["Kaffee", "Tee", "Kakao", "Saft"],
        series: [{ name: "Verkäufe", values: [320, 240, 180, 95] }],
      };
    default:
      return {
        title: "Umsatz je Quartal",
        unit: "€",
        labels: ["Q1", "Q2", "Q3", "Q4"],
        series: [
          { name: "2025", values: [42, 51, 48, 63] },
          { name: "2026", values: [48, 57, 61, 72] },
        ],
      };
  }
}

// ---------------------------------------------------------------------------
// Geometry
// ---------------------------------------------------------------------------

export type ChartShape =
  | {
      kind: "path";
      d: string;
      fill?: string;
      fillOpacity?: number;
      stroke?: string;
      strokeWidth?: number;
    }
  | { kind: "circle"; cx: number; cy: number; r: number; fill: string }
  | {
      kind: "line";
      x1: number;
      y1: number;
      x2: number;
      y2: number;
      stroke: string;
      width: number;
    }
  | {
      kind: "text";
      x: number;
      y: number;
      text: string;
      size: number;
      weight: number;
      anchor: "start" | "middle" | "end";
      fill: string;
    };

export interface ChartScene {
  width: number;
  height: number;
  fontFamily: string;
  shapes: ChartShape[];
}

/** Rough text width (no DOM in shared code): average glyph ≈ 0.56 em. */
export function chartTextWidth(text: string, size: number): number {
  let units = 0;
  for (const char of text) {
    if (/[il.,:;'|!  ]/.test(char)) units += 0.3;
    else if (/[mwMW@%€]/.test(char)) units += 0.85;
    else if (/[A-ZÄÖÜ0-9]/.test(char)) units += 0.64;
    else units += 0.54;
  }
  return units * size;
}

const r2 = (value: number) => Math.round(value * 100) / 100;

/** Nice round axis steps (1, 2, 2.5, 5 × 10ⁿ). */
export function niceTicks(min: number, max: number, count = 5): number[] {
  if (!(max > min)) return [min];
  const raw = (max - min) / Math.max(1, count);
  const power = 10 ** Math.floor(Math.log10(raw));
  const step =
    [1, 2, 2.5, 5, 10].map((factor) => factor * power).find((candidate) => candidate >= raw) ??
    10 * power;
  const start = Math.floor(min / step + 1e-9) * step;
  const end = Math.ceil(max / step - 1e-9) * step;
  const ticks: number[] = [];
  for (let value = start; value <= end + step / 2 && ticks.length < 50; value += step) {
    ticks.push(Math.round(value / step) * step);
  }
  return ticks;
}

/** Path of a rectangle with rounded corners on one end (`end`: where the bar grows to). */
function barPath(
  x: number,
  y: number,
  w: number,
  h: number,
  radius: number,
  end: "top" | "bottom" | "right" | "left" | "none",
): string {
  const r = Math.max(
    0,
    Math.min(
      radius,
      end === "top" || end === "bottom" ? w / 2 : h / 2,
      end === "top" || end === "bottom" ? h : w,
    ),
  );
  const [x0, y0, x1, y1] = [r2(x), r2(y), r2(x + w), r2(y + h)];
  if (r < 0.01 || end === "none") return `M${x0} ${y0}H${x1}V${y1}H${x0}Z`;
  const q = r2(r);
  switch (end) {
    case "top":
      return `M${x0} ${y1}V${r2(y0 + q)}Q${x0} ${y0} ${r2(x0 + q)} ${y0}H${r2(x1 - q)}Q${x1} ${y0} ${x1} ${r2(y0 + q)}V${y1}Z`;
    case "bottom":
      return `M${x0} ${y0}H${x1}V${r2(y1 - q)}Q${x1} ${y1} ${r2(x1 - q)} ${y1}H${r2(x0 + q)}Q${x0} ${y1} ${x0} ${r2(y1 - q)}Z`;
    case "right":
      return `M${x0} ${y0}H${r2(x1 - q)}Q${x1} ${y0} ${x1} ${r2(y0 + q)}V${r2(y1 - q)}Q${x1} ${y1} ${r2(x1 - q)} ${y1}H${x0}Z`;
    case "left":
      return `M${x1} ${y0}V${y1}H${r2(x0 + q)}Q${x0} ${y1} ${x0} ${r2(y1 - q)}V${r2(y0 + q)}Q${x0} ${y0} ${r2(x0 + q)} ${y0}Z`;
  }
}

/** Monotone cubic interpolation (Fritsch–Carlson): smooth, no overshoot. */
function linePath(points: { x: number; y: number }[], smooth: boolean): string {
  if (points.length === 0) return "";
  const first = points[0] as { x: number; y: number };
  let d = `M${r2(first.x)} ${r2(first.y)}`;
  if (!smooth || points.length < 3) {
    for (const point of points.slice(1)) d += `L${r2(point.x)} ${r2(point.y)}`;
    return d;
  }
  const n = points.length;
  const dx: number[] = [];
  const slope: number[] = [];
  for (let i = 0; i < n - 1; i++) {
    const a = points[i] as { x: number; y: number };
    const b = points[i + 1] as { x: number; y: number };
    dx.push(b.x - a.x);
    slope.push(dx[i] === 0 ? 0 : (b.y - a.y) / (dx[i] as number));
  }
  const tangent: number[] = [slope[0] as number];
  for (let i = 1; i < n - 1; i++) {
    const s0 = slope[i - 1] as number;
    const s1 = slope[i] as number;
    tangent.push(s0 * s1 <= 0 ? 0 : (3 * (s0 + s1)) / ((2 * s1 + s0) / s0 + (s1 + 2 * s0) / s1));
  }
  tangent.push(slope[n - 2] as number);
  for (let i = 0; i < n - 1; i++) {
    const a = points[i] as { x: number; y: number };
    const b = points[i + 1] as { x: number; y: number };
    const h = (dx[i] as number) / 3;
    d += `C${r2(a.x + h)} ${r2(a.y + h * (tangent[i] as number))} ${r2(b.x - h)} ${r2(b.y - h * (tangent[i + 1] as number))} ${r2(b.x)} ${r2(b.y)}`;
  }
  return d;
}

function polar(cx: number, cy: number, radius: number, angle: number) {
  return { x: r2(cx + radius * Math.cos(angle)), y: r2(cy + radius * Math.sin(angle)) };
}

/** Pie slice or ring segment from angle a0 to a1 (radians, clockwise, 0 = 3 o'clock). */
function arcPath(
  cx: number,
  cy: number,
  outer: number,
  inner: number,
  a0: number,
  a1: number,
): string {
  const sweep = a1 - a0;
  if (sweep >= Math.PI * 2 - 1e-6) {
    // Full circle: two half arcs (an arc cannot end where it starts).
    const o0 = polar(cx, cy, outer, a0);
    const o1 = polar(cx, cy, outer, a0 + Math.PI);
    let d = `M${o0.x} ${o0.y}A${r2(outer)} ${r2(outer)} 0 1 1 ${o1.x} ${o1.y}A${r2(outer)} ${r2(outer)} 0 1 1 ${o0.x} ${o0.y}Z`;
    if (inner > 0) {
      const i0 = polar(cx, cy, inner, a0);
      const i1 = polar(cx, cy, inner, a0 + Math.PI);
      d += `M${i0.x} ${i0.y}A${r2(inner)} ${r2(inner)} 0 1 0 ${i1.x} ${i1.y}A${r2(inner)} ${r2(inner)} 0 1 0 ${i0.x} ${i0.y}Z`;
    }
    return d;
  }
  const large = sweep > Math.PI ? 1 : 0;
  const o0 = polar(cx, cy, outer, a0);
  const o1 = polar(cx, cy, outer, a1);
  if (inner <= 0) {
    return `M${r2(cx)} ${r2(cy)}L${o0.x} ${o0.y}A${r2(outer)} ${r2(outer)} 0 ${large} 1 ${o1.x} ${o1.y}Z`;
  }
  const i1 = polar(cx, cy, inner, a1);
  const i0 = polar(cx, cy, inner, a0);
  return `M${o0.x} ${o0.y}A${r2(outer)} ${r2(outer)} 0 ${large} 1 ${o1.x} ${o1.y}L${i1.x} ${i1.y}A${r2(inner)} ${r2(inner)} 0 ${large} 0 ${i0.x} ${i0.y}Z`;
}

/** Solid, print-safe (no transparency) muted variant of the text colour for grid lines. */
function muted(color: string, amount: number): string {
  const base = opaqueHex(color);
  return mixColors(base, relativeLuminance(base) > 0.4 ? "#000000" : "#ffffff", amount);
}

/** Readable label colour on a coloured area. */
function labelOn(fill: string): string {
  return relativeLuminance(fill) > 0.45 ? "#1a1a1a" : "#ffffff";
}

interface LegendItem {
  label: string;
  color: string;
}

/** Lays out the legend and returns the space it takes. */
function layoutLegend(
  items: LegendItem[],
  position: "top" | "bottom" | "right",
  area: { x: number; y: number; w: number; h: number },
  size: number,
  color: string,
  shapes: ChartShape[],
): { x: number; y: number; w: number; h: number } {
  const dot = size * 0.36;
  const gap = size * 0.9;
  const itemWidth = (item: LegendItem) => dot * 2 + size * 0.4 + chartTextWidth(item.label, size);
  const lineHeight = size * 1.5;
  const draw = (item: LegendItem, x: number, y: number) => {
    shapes.push({ kind: "circle", cx: r2(x + dot), cy: r2(y), r: r2(dot), fill: item.color });
    shapes.push({
      kind: "text",
      x: r2(x + dot * 2 + size * 0.4),
      y: r2(y),
      text: item.label,
      size: r2(size),
      weight: 400,
      anchor: "start",
      fill: color,
    });
  };
  if (position === "right") {
    const width = Math.min(area.w * 0.4, Math.max(...items.map(itemWidth)));
    const height = items.length * lineHeight;
    const x = area.x + area.w - width;
    let y = area.y + Math.max(0, (area.h - height) / 2) + lineHeight / 2;
    for (const item of items) {
      draw(item, x, y);
      y += lineHeight;
    }
    return { ...area, w: Math.max(0, area.w - width - gap) };
  }
  // Rows, centred, wrapping.
  const rows: LegendItem[][] = [[]];
  let used = 0;
  for (const item of items) {
    const w = itemWidth(item) + gap;
    const row = rows[rows.length - 1] as LegendItem[];
    if (row.length > 0 && used + w - gap > area.w) {
      rows.push([item]);
      used = w;
    } else {
      row.push(item);
      used += w;
    }
  }
  const height = rows.length * lineHeight;
  let y = position === "top" ? area.y + lineHeight / 2 : area.y + area.h - height + lineHeight / 2;
  for (const row of rows) {
    const total = row.reduce((sum, item) => sum + itemWidth(item) + gap, -gap);
    let x = area.x + Math.max(0, (area.w - total) / 2);
    for (const item of row) {
      draw(item, x, y);
      x += itemWidth(item) + gap;
    }
    y += lineHeight;
  }
  const space = height + size * 0.6;
  return position === "top"
    ? { x: area.x, y: area.y + space, w: area.w, h: Math.max(0, area.h - space) }
    : { ...area, h: Math.max(0, area.h - space) };
}

/**
 * Geometry of a chart in its own coordinate system (0,0 = top-left of the
 * element box, units = element pixels). Texts are positioned by their
 * vertical centre (`dominant-baseline: central` in the renderer).
 */
export function chartLayout(element: ChartElement): ChartScene {
  const { width, height } = element;
  const shapes: ChartShape[] = [];
  const size = Math.max(4, element.fontSize);
  const color = opaqueHex(element.color);
  const scene: ChartScene = { width, height, fontFamily: element.fontFamily, shapes };
  let area = { x: 0, y: 0, w: width, h: height };

  if (element.kind === "progress") {
    layoutProgress(element, area, shapes);
    return scene;
  }

  if (element.title.trim()) {
    const titleSize = size * 1.3;
    shapes.push({
      kind: "text",
      x: 0,
      y: r2(titleSize * 0.6),
      text: element.title.trim(),
      size: r2(titleSize),
      weight: 700,
      anchor: "start",
      fill: color,
    });
    const used = titleSize * 1.2 + size * 0.8;
    area = { x: 0, y: used, w: width, h: Math.max(0, height - used) };
  }

  const radial = element.kind === "pie" || element.kind === "donut";
  const labels = element.labels;
  const legendItems: LegendItem[] = radial
    ? labels.map((label, index) => ({ label, color: chartColor(element, index) }))
    : element.series.map((series, index) => ({
        label: series.name,
        color: seriesColor(element, series, index),
      }));
  if (element.legend !== "none" && legendItems.some((item) => item.label.trim())) {
    area = layoutLegend(legendItems, element.legend, area, size * 0.9, color, shapes);
  }
  if (area.w < 4 || area.h < 4) return scene;

  if (radial) layoutRadial(element, area, shapes);
  else layoutCartesian(element, area, shapes);
  return scene;
}

function layoutProgress(
  element: ChartElement,
  area: { x: number; y: number; w: number; h: number },
  shapes: ChartShape[],
) {
  const value = element.series[0]?.values[0] ?? 0;
  const max = element.max ?? 100;
  const share = Math.max(0, Math.min(1, (value ?? 0) / max));
  const label = element.labels[0]?.trim() ?? "";
  const color = opaqueHex(element.color);
  const main = chartColor(element, 0);
  const outer = Math.max(1, Math.min(area.w, area.h) / 2);
  const inner = outer * element.hole;
  const stroke = outer - inner;
  const mid = (outer + inner) / 2;
  const cx = area.x + area.w / 2;
  const cy = area.y + area.h / 2;
  // Track: a light, solid tint of the colour (print-safe, no transparency).
  shapes.push({
    kind: "path",
    d: arcPath(cx, cy, outer, inner, 0, Math.PI * 2),
    fill: mixColors(opaqueHex(main), "#ffffff", 0.82),
  });
  const start = -Math.PI / 2;
  if (share > 0.0005) {
    if (share >= 0.9995) {
      shapes.push({ kind: "path", d: arcPath(cx, cy, outer, inner, 0, Math.PI * 2), fill: main });
    } else {
      const a1 = start + share * Math.PI * 2;
      const p0 = polar(cx, cy, mid, start);
      const p1 = polar(cx, cy, mid, a1);
      shapes.push({
        kind: "path",
        d: `M${p0.x} ${p0.y}A${r2(mid)} ${r2(mid)} 0 ${share > 0.5 ? 1 : 0} 1 ${p1.x} ${p1.y}`,
        stroke: main,
        strokeWidth: r2(stroke),
      });
    }
  }
  const valueSize = inner * (label ? 0.5 : 0.6);
  const text = formatChartNumber(value ?? 0, element.decimals, element.unit);
  const fitted = Math.min(valueSize, (inner * 1.7) / Math.max(1, chartTextWidth(text, 1)));
  shapes.push({
    kind: "text",
    x: r2(cx),
    y: r2(label ? cy - fitted * 0.2 : cy),
    text,
    size: r2(fitted),
    weight: 700,
    anchor: "middle",
    fill: color,
  });
  if (label) {
    const labelSize = Math.min(
      Math.max(4, element.fontSize),
      (inner * 1.5) / Math.max(1, chartTextWidth(label, 1)),
    );
    shapes.push({
      kind: "text",
      x: r2(cx),
      y: r2(cy + fitted * 0.55 + labelSize * 0.3),
      text: label,
      size: r2(labelSize),
      weight: 400,
      anchor: "middle",
      fill: muted(color, 0.3),
    });
  }
}

function layoutRadial(
  element: ChartElement,
  area: { x: number; y: number; w: number; h: number },
  shapes: ChartShape[],
) {
  const values = element.labels.map((_, index) =>
    Math.max(0, element.series[0]?.values[index] ?? 0),
  );
  const total = values.reduce((sum, value) => sum + value, 0);
  const outer = Math.min(area.w, area.h) / 2;
  const inner = element.kind === "donut" ? outer * element.hole : 0;
  const cx = area.x + area.w / 2;
  const cy = area.y + area.h / 2;
  if (total <= 0 || outer <= 0) {
    shapes.push({
      kind: "path",
      d: arcPath(cx, cy, outer, inner, 0, Math.PI * 2),
      fill: muted(element.color, 0.85),
    });
    return;
  }
  const gap = element.kind === "donut" ? Math.min(outer * 0.02, 6) : 0;
  let angle = -Math.PI / 2;
  const labelSize = Math.max(4, element.fontSize * 0.85);
  for (const [index, value] of values.entries()) {
    if (value <= 0) continue;
    const sweep = (value / total) * Math.PI * 2;
    const a0 = angle;
    const a1 = angle + sweep;
    angle = a1;
    const fill = chartColor(element, index);
    const pad = gap > 0 && sweep < Math.PI * 2 - 1e-6 ? gap / outer / 2 : 0;
    shapes.push({
      kind: "path",
      d: arcPath(cx, cy, outer, inner, a0 + pad, Math.max(a0 + pad + 1e-4, a1 - pad)),
      fill,
    });
    if (element.showValues && value / total >= 0.04) {
      const at = polar(cx, cy, inner > 0 ? (outer + inner) / 2 : outer * 0.62, (a0 + a1) / 2);
      shapes.push({
        kind: "text",
        x: at.x,
        y: at.y,
        text: formatChartNumber(value, element.decimals, element.unit),
        size: r2(Math.min(labelSize, inner > 0 ? (outer - inner) * 0.55 : labelSize)),
        weight: 700,
        anchor: "middle",
        fill: labelOn(fill),
      });
    }
  }
}

function layoutCartesian(
  element: ChartElement,
  area: { x: number; y: number; w: number; h: number },
  shapes: ChartShape[],
) {
  const horizontal = element.kind === "bar";
  const stacked = element.stacked && element.kind !== "line";
  const n = element.labels.length;
  const size = Math.max(4, element.fontSize);
  const color = opaqueHex(element.color);
  const tickColor = muted(color, 0.35);
  const series = element.series;
  const value = (s: ChartSeries, index: number) => s.values[index] ?? null;

  // Value range
  let lo = 0;
  let hi = 0;
  for (let i = 0; i < n; i++) {
    if (stacked) {
      let pos = 0;
      let neg = 0;
      for (const s of series) {
        const v = value(s, i) ?? 0;
        if (v >= 0) pos += v;
        else neg += v;
      }
      hi = Math.max(hi, pos);
      lo = Math.min(lo, neg);
    } else {
      for (const s of series) {
        const v = value(s, i);
        if (v === null) continue;
        hi = Math.max(hi, v);
        lo = Math.min(lo, v);
      }
    }
  }
  if (element.max !== undefined) hi = Math.max(element.max, lo + 1e-9);
  if (hi === lo) hi = lo + 1;
  const ticks = niceTicks(lo, hi, horizontal ? 4 : 5);
  const min = Math.min(lo, ticks[0] as number);
  const max = element.max ?? Math.max(hi, ticks[ticks.length - 1] as number);
  const shown = ticks.filter((tick) => tick >= min - 1e-9 && tick <= max + 1e-9);
  const format = (v: number) => formatChartNumber(v, element.decimals, element.unit);
  const tickSize = size * 0.8;

  // Plot rectangle: room for axis labels
  const categoryWidth = horizontal
    ? Math.min(
        area.w * 0.35,
        Math.max(0, ...element.labels.map((l) => chartTextWidth(l, size * 0.85))) + size * 0.6,
      )
    : 0;
  const valueLabelWidth = element.axis
    ? Math.max(...shown.map((tick) => chartTextWidth(format(tick), tickSize))) + size * 0.5
    : 0;
  const left = area.x + (horizontal ? categoryWidth : valueLabelWidth);
  const bottomSpace = element.axis || horizontal ? size * 1.6 : size * 0.2;
  const top = area.y + (element.showValues && !horizontal ? size * 1.2 : size * 0.4);
  const right = area.x + area.w - (element.showValues && horizontal ? size * 3 : size * 0.3);
  const bottom =
    area.y + area.h - (horizontal ? (element.axis ? size * 1.4 : size * 0.2) : bottomSpace);
  const plotW = Math.max(1, right - left);
  const plotH = Math.max(1, bottom - top);
  const scale = (v: number) =>
    horizontal
      ? left + ((v - min) / (max - min)) * plotW
      : bottom - ((v - min) / (max - min)) * plotH;

  // Grid and value axis
  for (const tick of shown) {
    const at = r2(scale(tick));
    if (element.gridlines || tick === 0) {
      shapes.push(
        horizontal
          ? {
              kind: "line",
              x1: at,
              y1: r2(top),
              x2: at,
              y2: r2(bottom),
              stroke: tick === 0 ? muted(color, 0.55) : muted(color, 0.85),
              width: tick === 0 ? 1.5 : 1,
            }
          : {
              kind: "line",
              x1: r2(left),
              y1: at,
              x2: r2(right),
              y2: at,
              stroke: tick === 0 ? muted(color, 0.55) : muted(color, 0.85),
              width: tick === 0 ? 1.5 : 1,
            },
      );
    }
    if (element.axis) {
      shapes.push(
        horizontal
          ? {
              kind: "text",
              x: at,
              y: r2(bottom + size * 0.8),
              text: format(tick),
              size: r2(tickSize),
              weight: 400,
              anchor: "middle",
              fill: tickColor,
            }
          : {
              kind: "text",
              x: r2(left - size * 0.4),
              y: at,
              text: format(tick),
              size: r2(tickSize),
              weight: 400,
              anchor: "end",
              fill: tickColor,
            },
      );
    }
  }
  if (n === 0) return;

  const band = (horizontal ? plotH : plotW) / n;
  const categoryAt = (index: number) => (horizontal ? top : left) + band * (index + 0.5);

  // Category labels (skip some when they would overlap)
  const labelSize = size * 0.85;
  const widest = Math.max(...element.labels.map((l) => chartTextWidth(l, labelSize)));
  const every = horizontal
    ? Math.max(1, Math.ceil((labelSize * 1.2) / band))
    : Math.max(1, Math.ceil((widest + size * 0.5) / band));
  if (element.axis || horizontal) {
    for (const [index, label] of element.labels.entries()) {
      if (index % every !== 0) continue;
      shapes.push(
        horizontal
          ? {
              kind: "text",
              x: r2(left - size * 0.4),
              y: r2(categoryAt(index)),
              text: label,
              size: r2(labelSize),
              weight: 400,
              anchor: "end",
              fill: color,
            }
          : {
              kind: "text",
              x: r2(categoryAt(index)),
              y: r2(bottom + size * 0.85),
              text: label,
              size: r2(labelSize),
              weight: 400,
              anchor: "middle",
              fill: color,
            },
      );
    }
  }

  const valueSize = size * 0.75;
  if (element.kind === "column" || element.kind === "bar") {
    const group = band * (stacked || series.length === 1 ? 0.6 : 0.74);
    const count = stacked ? 1 : series.length;
    const barGap = count > 1 ? Math.min(group * 0.06, size * 0.3) : 0;
    const thickness = Math.max(0.5, (group - barGap * (count - 1)) / count);
    for (let i = 0; i < n; i++) {
      let pos = 0;
      let neg = 0;
      // Outermost stacked segment per direction gets the rounded end.
      const lastPos = stacked ? series.findLastIndex((s) => (value(s, i) ?? 0) > 0) : -1;
      const lastNeg = stacked ? series.findLastIndex((s) => (value(s, i) ?? 0) < 0) : -1;
      for (const [si, s] of series.entries()) {
        const v = value(s, i);
        if (v === null || v === 0) continue;
        const fill = seriesColor(element, s, si);
        const from = stacked ? (v > 0 ? pos : neg) : 0;
        const to = from + v;
        if (stacked) {
          if (v > 0) pos = to;
          else neg = to;
        }
        const a = scale(Math.max(min, Math.min(max, from)));
        const b = scale(Math.max(min, Math.min(max, to)));
        const offset = categoryAt(i) - group / 2 + (stacked ? 0 : si * (thickness + barGap));
        const rounded = !stacked || si === (v > 0 ? lastPos : lastNeg);
        const radius = rounded ? element.barRadius : 0;
        const d = horizontal
          ? barPath(
              Math.min(a, b),
              offset,
              Math.abs(b - a),
              thickness,
              radius,
              v > 0 ? "right" : "left",
            )
          : barPath(
              offset,
              Math.min(a, b),
              thickness,
              Math.abs(b - a),
              radius,
              v > 0 ? "top" : "bottom",
            );
        shapes.push({ kind: "path", d, fill });
        if (element.showValues) {
          const text = format(v);
          if (stacked) {
            const length = Math.abs(b - a);
            const fits = horizontal
              ? length > chartTextWidth(text, valueSize) + 4
              : length > valueSize * 1.3;
            if (fits && thickness > valueSize * (horizontal ? 1.1 : 0.1)) {
              shapes.push({
                kind: "text",
                x: r2(horizontal ? (a + b) / 2 : offset + thickness / 2),
                y: r2(horizontal ? offset + thickness / 2 : (a + b) / 2),
                text,
                size: r2(valueSize),
                weight: 700,
                anchor: "middle",
                fill: labelOn(fill),
              });
            }
          } else {
            shapes.push(
              horizontal
                ? {
                    kind: "text",
                    x: r2(v > 0 ? b + size * 0.3 : b - size * 0.3),
                    y: r2(offset + thickness / 2),
                    text,
                    size: r2(valueSize),
                    weight: 700,
                    anchor: v > 0 ? "start" : "end",
                    fill: color,
                  }
                : {
                    kind: "text",
                    x: r2(offset + thickness / 2),
                    y: r2(v > 0 ? b - valueSize * 0.8 : b + valueSize * 0.8),
                    text,
                    size: r2(
                      Math.min(valueSize, (thickness * 1.6) / Math.max(1, chartTextWidth(text, 1))),
                    ),
                    weight: 700,
                    anchor: "middle",
                    fill: color,
                  },
            );
          }
        }
      }
    }
    return;
  }

  // Lines and areas
  const baseline = scale(Math.max(min, Math.min(max, 0)));
  const running = new Array<number>(n).fill(0);
  const layers: {
    fill: string;
    line: string;
    area?: string;
    points: { x: number; y: number; v: number }[];
  }[] = [];
  for (const [si, s] of series.entries()) {
    const fill = seriesColor(element, s, si);
    const segments: { x: number; y: number; v: number; base: number }[][] = [[]];
    for (let i = 0; i < n; i++) {
      const v = value(s, i);
      if (v === null) {
        if ((segments[segments.length - 1] as unknown[]).length > 0) segments.push([]);
        continue;
      }
      const base = stacked ? (running[i] as number) : 0;
      const top = base + v;
      if (stacked) running[i] = top;
      (segments[segments.length - 1] as { x: number; y: number; v: number; base: number }[]).push({
        x: categoryAt(i),
        y: scale(Math.max(min, Math.min(max, top))),
        v,
        base: scale(Math.max(min, Math.min(max, base))),
      });
    }
    let line = "";
    let areaPath = "";
    const points: { x: number; y: number; v: number }[] = [];
    for (const segment of segments) {
      if (segment.length === 0) continue;
      const path = linePath(segment, element.smooth);
      line += path;
      points.push(...segment);
      if (element.kind === "area") {
        const first = segment[0] as { x: number };
        const last = segment[segment.length - 1] as { x: number };
        if (stacked) {
          const lower = [...segment].reverse().map((p) => ({ x: p.x, y: p.base }));
          areaPath += `${path}${linePath(lower, element.smooth).replace(/^M/, "L")}Z`;
        } else {
          areaPath += `${path}L${r2(last.x)} ${r2(baseline)}L${r2(first.x)} ${r2(baseline)}Z`;
        }
      }
    }
    layers.push({ fill, line, ...(areaPath ? { area: areaPath } : {}), points });
  }
  // Areas first (so lines stay on top), later series above earlier ones.
  for (const layer of layers) {
    if (layer.area) {
      shapes.push(
        stacked
          ? { kind: "path", d: layer.area, fill: mixColors(opaqueHex(layer.fill), "#ffffff", 0.25) }
          : { kind: "path", d: layer.area, fill: opaqueHex(layer.fill), fillOpacity: 0.22 },
      );
    }
  }
  const lineWidth = element.lineWidth;
  for (const layer of layers) {
    if (layer.line)
      shapes.push({ kind: "path", d: layer.line, stroke: layer.fill, strokeWidth: r2(lineWidth) });
    if (element.points) {
      for (const point of layer.points) {
        shapes.push({
          kind: "circle",
          cx: r2(point.x),
          cy: r2(point.y),
          r: r2(Math.max(lineWidth * 1.1, 2)),
          fill: layer.fill,
        });
      }
    }
    if (element.showValues) {
      for (const point of layer.points) {
        shapes.push({
          kind: "text",
          x: r2(point.x),
          y: r2(point.y - Math.max(lineWidth * 1.1, 2) - valueSize * 0.75),
          text: format(point.v),
          size: r2(valueSize),
          weight: 700,
          anchor: "middle",
          fill: color,
        });
      }
    }
  }
}

/** Does the chart draw see-through areas? (Print: needs PDF/X-4.) */
export function chartHasTransparency(element: ChartElement): boolean {
  return element.kind === "area" && !element.stacked;
}

/** All texts of a chart (search, preflight labels). */
export function chartTexts(element: ChartElement): string[] {
  return [element.title, ...element.labels, ...element.series.map((s) => s.name)].filter(
    (text) => text.trim() !== "",
  );
}
